import { BasePromptPage } from "./base"
import {useContext, useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import apiRequest from "@services/apiRequest";
import {UserWithRoleSchema} from "@feature/types";
import {FormattedMessage, useIntl} from "react-intl";
import {useMergeAccounts} from "@feature/users/hooks/useMergeAccounts";
import {NotificationsContext} from "@ui-components/Notifications";
import AuthenticationService from "@services/AuthenticationService";
import CountryContext from "../../CountryContext";

export const MergeAccountsPrompt = () => {

  // context
  const {push} = useContext(NotificationsContext);
  const {country: {code}} = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const history = useHistory();
  const {token} = useParams<{token: string}>();
  const {merge, loading} = useMergeAccounts();

  // state
  const [state, setState]
    = useState<Record<string, UserWithRoleSchema>>();

  // utilities
  const decoded = JSON.parse(atob(token));

  // handlers
  const loadUsersIntoState = async () => {
    // 1. Retrieve the user that has requested the merge and the one in which the current user will be merged into
    const [requester, destination] = await Promise.all(
      ["requester_id", "destination_id"].map((p) =>
        apiRequest.get(`/users/${decoded[p]}`) as Promise<UserWithRoleSchema>
      )
    );
    if (!!destination.merged_in)
      history.push("/");
    setState({requester, destination});
  }

  useEffect(() => {
    loadUsersIntoState().then();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BasePromptPage
      title={
        <FormattedMessage
          id="merge_account_request"
          defaultMessage="Richiesta di conversione utenza"
        />
      }
      loading={!state}
      submitting={loading}
      onConfirm={async () => {
        try {
          const {access_token, user_data} = await merge(decoded.destination_id);
          AuthenticationService.setAuthToken(access_token, user_data);

          push({type: "success", title: intl.formatMessage({id: "account_merged_success"})});
          history.push(`/${code}/home`)
        }
        catch (e) {
          push({type: "error", title: intl.formatMessage({id: "server_error"})});
          throw e;
        }
      }}
    >
      <div className="flex flex-col gap-5">
        <p>
          <FormattedMessage
            id="merge_account_request_description"
            defaultMessage="L'utente {name} ha richiesto di convertire il tuo account nella seguente utenza"
            values={{
              name: <span className="font-semibold">{state?.["requester"].full_name}</span>,
              email: <span className="font-semibold">{state?.["requester"].email}</span>,
            }}
          />
        </p>
        <ul className="list-disc list-inside">
          <li className="font-semibold">
            {state?.["destination"].full_name} &mdash; <FormattedMessage id={state?.["destination"].role.name} />
          </li>
        </ul>
        <p>
          <FormattedMessage
            id="click_confirm_to_proceed"
            defaultMessage="Clicca su CONFERMA per procedere con l'operazione"
          />
        </p>
      </div>
    </BasePromptPage>
  )
}